import {
  dataModelerUpdateJoinData,
  IJoinDraftData,
  IJoinPairConditionDraftData,
  ITemporalJoinConditionDraftData,
} from '../../../../redux/slices/dataModeler';
import {
  FieldClass,
  FieldType,
  ICustomJoinMeta,
  IDimensionFieldsFragment,
  ITableColumnFieldsFragment,
  ITableJoinMetaFieldsFragment,
  JoinConditionOperator,
  TemporalJoinType,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
} from '../../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import { assertNotNil } from '../../../../utils/types';
import { useCallback, useMemo } from 'react';
import { SchemaFieldMeta } from '../SchemaEntityRelations/types';
import produce from 'immer';

export interface IEditJoinModel {
  joinData: IJoinDraftData;
  addCondition: (
    factFieldKey: string,
    dimTableColumn: string,
    relOp: JoinConditionOperator,
    isSubject: boolean,
  ) => void;
  removeCondition: (index: number) => void;
  updateCondition: (
    index: number,
    factFieldKey: string,
    dimTableColumn: string,
    relOp: JoinConditionOperator,
  ) => void;
  setTemporalCondition: (
    temporalCondition: ITemporalJoinConditionDraftData | null,
  ) => void;
  setSqlCondition: (sqlCondition: string | null) => void;
}

const dimFieldToTableColumn = (
  dimField: SchemaFieldMeta,
): ITableColumnFieldsFragment => {
  return {
    column: dimField.tableFields[0].column,
    type: dimField.tableFields[0].type,
    description: dimField.description,
    typeParams: {
      __typename: 'FieldTypeParams',
      fixedTz: null,
      linearDistribution: false,
      arrayItemType: FieldType.String,
      customDefinition: null,
      structDefinition: null,
      mapDefinition: null,
    },
    __typename: 'TableColumn',
  };
};

const temporalJoinMetaToDraftData = (
  dimData: IDimensionFieldsFragment,
  joinMeta: ITableJoinMetaFieldsFragment,
): ITemporalJoinConditionDraftData | null => {
  const temporalConditionMeta = joinMeta.temporalCondition;
  if (!temporalConditionMeta) {
    return null;
  }
  const dimTemporalKey = temporalConditionMeta.dimTemporalKeys[0];
  if (!dimTemporalKey) {
    return null;
  }
  const dimTemporalFirstField = dimData.fields.find(
    (dimField) => dimField.key === dimTemporalKey,
  );
  assertNotNil(dimTemporalFirstField);
  const dimTemporalColumn = dimFieldToTableColumn(dimTemporalFirstField);
  const dimSecondTemporalKey =
    temporalConditionMeta.dimTemporalKeys.length > 1
      ? temporalConditionMeta.dimTemporalKeys[1]
      : null;
  const dimTemporalSecondField = dimData.fields.find(
    (dimField) => dimField.key === dimSecondTemporalKey,
  );
  const dimSecondTemporalColumn = dimTemporalSecondField
    ? dimFieldToTableColumn(dimTemporalSecondField)
    : null;
  return {
    joinType: temporalConditionMeta.temporalJoinType,
    factTemporalKey: temporalConditionMeta.factTemporalKey,
    dimTemporalColumn: dimTemporalColumn.column,
    dimSecondTemporalColumn: dimSecondTemporalColumn?.column ?? null,
  };
};

const joinMetaToJoinDraftData = (
  joinMeta: ITableJoinMetaFieldsFragment,
  dimData: IDimensionFieldsFragment,
  factSubjectKeys: Set<string>,
): IJoinDraftData => {
  const temporalCondition = temporalJoinMetaToDraftData(dimData, joinMeta);
  return {
    conditions: joinMeta.joinPairs.map((jp): IJoinPairConditionDraftData => {
      return {
        isSubject: factSubjectKeys.has(jp.factFieldKey),
        factFieldKey: jp.factFieldKey,
        dimTableColumn: jp.dimTableFieldColumn,
        relOp: jp.relOp,
      };
    }),
    sqlCondition: joinMeta.sqlCondition,
    temporalCondition,
  };
};

export const useEditJoinModel = (input: {
  viewKey: string;
  isEditMode: boolean;
}): {
  editModel: IEditJoinModel | null;
  customJoinMeta: ICustomJoinMeta | null;
} => {
  const { viewKey, isEditMode } = input;
  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;
  const dispatch = useAppDispatch();
  const factSubjectKeys = useMemo(() => {
    const ret = schemaObj.fact.fields
      .filter((field) => field.fieldClass === FieldClass.Subject)
      .map((field) => field.key);
    return new Set(ret);
  }, [schemaObj.fact.fields]);

  const editJoinData = useMemo(() => {
    const addedInput = draftEdits.addedDimensionsData.find(
      (dimData) => dimData.viewKey === viewKey,
    );
    if (addedInput) {
      return addedInput.joinData;
    }
    const editJoinData = draftEdits.editJoinsData.find(
      (eInput) => eInput.viewKey === viewKey,
    );
    return editJoinData?.joinData;
  }, [draftEdits.addedDimensionsData, draftEdits.editJoinsData, viewKey]);

  const existingDimData = useMemo(() => {
    return schemaObj.dimensions.find((dim) => dim.key === viewKey);
  }, [schemaObj.dimensions, viewKey]);

  const joinData = useMemo(
    () =>
      editJoinData ??
      (existingDimData && existingDimData.join.__typename === 'TableJoinMeta'
        ? joinMetaToJoinDraftData(
            existingDimData.join,
            existingDimData,
            factSubjectKeys,
          )
        : undefined),
    [editJoinData, existingDimData, factSubjectKeys],
  );

  const customJoinMeta = useMemo(
    () =>
      existingDimData && existingDimData.join.__typename === 'CustomJoinMeta'
        ? existingDimData.join
        : null,
    [existingDimData],
  );

  const addCondition = useCallback(
    (
      factFieldKey: string,
      dimTableColumn: string,
      relOp: JoinConditionOperator,
      isSubject: boolean,
    ) => {
      if (!isEditMode || !joinData) {
        return;
      }
      const newJoinData = {
        ...joinData,
        conditions: [
          ...joinData.conditions,
          { factFieldKey, dimTableColumn, isSubject, relOp },
        ],
      };
      dispatch(
        dataModelerUpdateJoinData({
          viewKey,
          joinData: newJoinData,
        }),
      );
    },
    [dispatch, isEditMode, joinData, viewKey],
  );

  const removeCondition = useCallback(
    (index: number) => {
      if (!isEditMode || !joinData) {
        return;
      }
      const newJoinData = produce(joinData, (draft) => {
        draft.conditions.splice(index, 1);
      });
      dispatch(
        dataModelerUpdateJoinData({
          viewKey,
          joinData: newJoinData,
        }),
      );
    },
    [dispatch, isEditMode, joinData, viewKey],
  );

  const updateCondition = useCallback(
    (
      index: number,
      factFieldKey: string,
      dimTableColumn: string,
      relOp: JoinConditionOperator,
    ) => {
      if (!isEditMode || !joinData) {
        return;
      }
      const newJoinData = produce(joinData, (draft) => {
        const cond = draft.conditions[index];
        cond.factFieldKey = factFieldKey;
        cond.dimTableColumn = dimTableColumn;
        cond.relOp = relOp;
      });
      dispatch(
        dataModelerUpdateJoinData({
          viewKey,
          joinData: newJoinData,
        }),
      );
    },
    [dispatch, isEditMode, joinData, viewKey],
  );

  const setTemporalCondition = useCallback(
    (temporalCondition: ITemporalJoinConditionDraftData | null) => {
      if (!isEditMode || !joinData) {
        return;
      }
      const newJoinData = {
        ...joinData,
        temporalCondition,
      };
      dispatch(
        dataModelerUpdateJoinData({
          viewKey,
          joinData: newJoinData,
        }),
      );
    },
    [dispatch, isEditMode, joinData, viewKey],
  );

  const setSqlCondition = useCallback(
    (sqlCondition: string | null) => {
      if (!isEditMode || !joinData) {
        return;
      }
      const newJoinData = {
        ...joinData,
        sqlCondition,
      };
      dispatch(
        dataModelerUpdateJoinData({
          viewKey,
          joinData: newJoinData,
        }),
      );
    },
    [dispatch, isEditMode, joinData, viewKey],
  );

  const editModel = useMemo(
    () =>
      joinData
        ? {
            joinData,
            addCondition,
            removeCondition,
            updateCondition,
            setTemporalCondition,
            setSqlCondition,
          }
        : null,
    [
      addCondition,
      joinData,
      removeCondition,
      setSqlCondition,
      setTemporalCondition,
      updateCondition,
    ],
  );
  return {
    customJoinMeta,
    editModel,
  };
};

export type JoinTypeOption = 'Subject' | 'Field' | 'Temporal';

export const JOIN_TYPE_SELECT_OPTS: {
  text: JoinTypeOption;
  value: JoinTypeOption;
}[] = [
  { text: 'Subject', value: 'Subject' },
  { text: 'Field', value: 'Field' },
  { text: 'Temporal', value: 'Temporal' },
];

export const JOIN_CONDITION_REL_OP_CONFIG: Record<
  JoinConditionOperator,
  {
    display: string;
  }
> = {
  [JoinConditionOperator.Equal]: {
    display: '=',
  },
  [JoinConditionOperator.GreaterThan]: {
    display: '>',
  },
  [JoinConditionOperator.GreaterThanOrEqual]: {
    display: '>=',
  },
  [JoinConditionOperator.LessThan]: {
    display: '<',
  },
  [JoinConditionOperator.LessThanOrEqual]: {
    display: '<=',
  },
  [JoinConditionOperator.NotEqual]: {
    display: '<>',
  },
};

export const JOIN_CONDITION_REL_OP_SELECT_OPTS: {
  value: JoinConditionOperator;
  text: string;
}[] = [
  {
    value: JoinConditionOperator.Equal,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.Equal].display,
  },
  {
    value: JoinConditionOperator.GreaterThan,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.GreaterThan]
      .display,
  },
  {
    value: JoinConditionOperator.GreaterThanOrEqual,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.GreaterThanOrEqual]
      .display,
  },
  {
    value: JoinConditionOperator.LessThan,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.LessThan].display,
  },
  {
    value: JoinConditionOperator.LessThanOrEqual,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.LessThanOrEqual]
      .display,
  },
  {
    value: JoinConditionOperator.NotEqual,
    text: JOIN_CONDITION_REL_OP_CONFIG[JoinConditionOperator.NotEqual].display,
  },
];

export const emptyTemporalCondition = (): ITemporalJoinConditionDraftData => {
  return {
    joinType: TemporalJoinType.LastEver,
    factTemporalKey: null,
    dimTemporalColumn: null,
    dimSecondTemporalColumn: null,
  };
};
